import {
  ClientPostCreationCreateRequestPostTypeEnum,
  type ClientPostChangeConfirmation,
  type ClientPostCreateConfirmation,
  type ClientPostCreationCreateRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback } from 'react';

import { useCreatePostCreation } from '@/features/api/hooks/grapevine-post-events/useCreatePostCreation';
import {
  PostCreationContext,
  type PostCreationChannelOption
} from '@/features/post-editor/types/postCreationOptions';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { getLocationDefinition } from '@/features/post-editor/utils/locationDefinition';
import { resolvePostType } from '@/features/post-editor/utils/resolvePostType';
import { toSpecialPostCustomAttributes } from '@/features/post-editor/utils/specialPostCustomAttributes';
import { isEmptyOrNull } from '@/shared/utils/string';

type CreatePostOptions = {
  creationContext: PostCreationContext;
  groupId?: string;
  organizationId?: string;
  senderChannels?: PostCreationChannelOption[];
  temporaryMediaItemIds: string[];
  temporaryDocumentItemIds: string[];
  onSuccess: (
    post: ClientPostCreateConfirmation | ClientPostChangeConfirmation
  ) => void;
};

const buildRequest = (
  values: PostFormValues,
  {
    creationContext,
    groupId,
    organizationId,
    senderChannels,
    temporaryMediaItemIds,
    temporaryDocumentItemIds
  }: Omit<CreatePostOptions, 'onSuccess'>
): ClientPostCreationCreateRequest | undefined => {
  const postType = resolvePostType(values);
  if (
    senderChannels !== undefined &&
    !senderChannels
      .find((channel) => channel.channelType === values.channelType)
      ?.postTypes.includes(postType)
  ) {
    return;
  }

  const isOrganizationPost = !isEmptyOrNull(organizationId);
  return {
    creationContext,
    postType,
    text: values.text.trim(),
    title: values.title?.trim() || undefined,
    hiddenForOtherHomeAreas: isOrganizationPost
      ? false
      : values.hiddenForOtherHomeAreas,
    temporaryDocumentItemIds,
    temporaryMediaItemIds,
    poll: values.poll,
    ...(isOrganizationPost
      ? {
          organizationId,
          geoAreaIds: values.geoAreaIds,
          categories:
            postType === ClientPostCreationCreateRequestPostTypeEnum.NewsItem
              ? values.categories
              : undefined
        }
      : {}),
    ...(creationContext === PostCreationContext.GRUPPE && groupId
      ? { groupId }
      : {}),
    ...(postType === ClientPostCreationCreateRequestPostTypeEnum.Happening
      ? {
          allDayEvent: Boolean(values.allDay),
          startTime: values.startDate?.getTime(),
          endTime: values.endDate?.getTime(),
          customLocationDefinition: getLocationDefinition(values.location)
        }
      : {}),
    ...(postType === ClientPostCreationCreateRequestPostTypeEnum.Suggestion
      ? {
          suggestionCategoryId: values.suggestionCategory?.id,
          customLocationDefinition: getLocationDefinition(values.location)
        }
      : {}),
    ...(postType === ClientPostCreationCreateRequestPostTypeEnum.SpecialPost
      ? {
          customAttributes: toSpecialPostCustomAttributes(values.dorfHelferType)
        }
      : {})
  };
};

export const useCreatePost = ({
  creationContext,
  groupId,
  organizationId,
  senderChannels,
  temporaryMediaItemIds,
  temporaryDocumentItemIds,
  onSuccess
}: CreatePostOptions) => {
  const createPost = useCreatePostCreation();

  return useCallback(
    (values: PostFormValues) => {
      if (creationContext === PostCreationContext.GRUPPE && !groupId) return;
      if (creationContext !== PostCreationContext.GRUPPE && groupId) return;

      const request = buildRequest(values, {
        creationContext,
        groupId,
        organizationId,
        senderChannels,
        temporaryMediaItemIds,
        temporaryDocumentItemIds
      });
      if (!request) return;

      createPost.mutate(request, { onSuccess });
    },
    [
      creationContext,
      groupId,
      organizationId,
      senderChannels,
      temporaryMediaItemIds,
      temporaryDocumentItemIds,
      createPost,
      onSuccess
    ]
  );
};
